"use client";

import { useMemo, useState } from "react";
import ArrowLink from "@/components/ui/ArrowLink";
import HelpTooltip from "@/components/ui/help-tooltip";
import Container from "@/components/ui/Container";
import Reveal from "@/components/ui/Reveal";
import SectionHeading from "@/components/ui/SectionHeading";
import { useHomeContent } from "@/lib/i18n/content-context";
import { useLocale } from "@/lib/i18n/locale-context";
import { localizeHref } from "@/lib/i18n/routing";
import { cn, formatCurrency } from "@/lib/utils";

function formatCompactCurrency(value: number) {
  if (value >= 1000) {
    return `$${Math.round(value / 1000)}K`;
  }

  return formatCurrency(value);
}

function RangeField({
  label,
  value,
  min,
  max,
  step,
  displayValue,
  minLabel,
  maxLabel,
  tooltip,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  displayValue: string;
  minLabel: string;
  maxLabel: string;
  tooltip?: string;
  onChange: (value: number) => void;
}) {
  const progress =
    max === min ? 0 : Math.round(((value - min) / (max - min)) * 100);

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-foreground">{label}</label>
          {tooltip ? <HelpTooltip text={tooltip} /> : null}
        </div>
        <p className="text-end text-lg font-semibold tracking-tight tabular-nums text-foreground md:text-xl">
          {displayValue}
        </p>
      </div>

      <div dir="ltr">
        <input
          type="range"
          className="roi-range mt-4 w-full"
          min={min}
          max={max}
          step={step}
          value={value}
          style={{ "--range-progress": `${progress}%` } as React.CSSProperties}
          aria-label={label}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          onChange={(event) => onChange(Number(event.target.value))}
        />

        <div className="mt-2 flex items-center justify-between text-xs tabular-nums text-muted-foreground">
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </div>
      </div>
    </div>
  );
}

function ResultRow({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "text-end text-sm font-semibold tabular-nums text-foreground",
          emphasis && "text-base",
        )}
      >
        {value}
      </dd>
    </div>
  );
}

export default function RoiCalculatorSection() {
  const locale = useLocale();
  const { roiContent } = useHomeContent();
  const [fraudLosses, setFraudLosses] = useState(
    roiContent.defaults.fraudLosses,
  );
  const [greipCosts, setGreipCosts] = useState(roiContent.defaults.greipCosts);

  const minimumReduction = roiContent.defaults.minReduction;

  const estimatedSavings = useMemo(
    () => fraudLosses * (minimumReduction / 100),
    [fraudLosses, minimumReduction],
  );

  const netBenefit = useMemo(
    () => estimatedSavings - greipCosts,
    [estimatedSavings, greipCosts],
  );

  const returnOnInvestment = useMemo(() => {
    if (greipCosts <= 0) return 0;
    return Math.round((netBenefit / greipCosts) * 100);
  }, [greipCosts, netBenefit]);

  return (
    <section className="bg-white py-20 md:py-28">
      <Container wide>
        <Reveal>
          <SectionHeading
            eyebrow={roiContent.eyebrow}
            title={roiContent.title}
            className="mb-8"
          />
        </Reveal>

        <div className="grid gap-8 lg:grid-cols-2 lg:items-start">
          <Reveal>
            <div>
              <header className="mb-4">
                <p className="text-xs font-semibold tracking-[0.12em] text-foreground uppercase">
                  {roiContent.inputsTitle}
                </p>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  {roiContent.inputsDescription}
                </p>
              </header>

              <div className="rounded-xl bg-foreground/[0.03] p-5 md:p-6">
                <div className="space-y-7">
                  <RangeField
                    label={roiContent.fraudLossesLabel}
                    value={fraudLosses}
                    min={500}
                    max={100000}
                    step={500}
                    displayValue={`${formatCurrency(fraudLosses)} / ${roiContent.annually}`}
                    minLabel={formatCurrency(500)}
                    maxLabel={formatCompactCurrency(100000)}
                    tooltip={roiContent.fraudLossesTooltip}
                    onChange={setFraudLosses}
                  />
                  <RangeField
                    label={roiContent.greipCostLabel}
                    value={greipCosts}
                    min={119.88}
                    max={1500}
                    step={0.01}
                    displayValue={`${formatCurrency(greipCosts)} / ${roiContent.annually}`}
                    minLabel={formatCurrency(119.88)}
                    maxLabel={formatCurrency(1500)}
                    onChange={setGreipCosts}
                  />
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.05}>
            <div>
              <header className="mb-4">
                <p className="text-xs font-semibold tracking-[0.12em] text-foreground uppercase">
                  {roiContent.resultsTitle}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {roiContent.resultsDescription}
                </p>
              </header>

              <div className="rounded-xl bg-[var(--ok)]/[0.06] px-5 py-4">
                <p className="text-sm font-medium text-foreground/75">
                  {roiContent.roiLabel}
                </p>
                <p className="mt-1 text-[clamp(2.25rem,5vw,3rem)] leading-none font-semibold tracking-tight tabular-nums text-[var(--ok)]">
                  {returnOnInvestment.toLocaleString()}%
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  {roiContent.netBenefitOfLabel}{" "}
                  <span className="font-semibold tabular-nums text-foreground">
                    {formatCurrency(Math.max(netBenefit, 0))}
                  </span>{" "}
                  / {roiContent.annually}
                </p>
              </div>

              <dl className="mt-4">
                <ResultRow
                  label={roiContent.estimatedSavingsLabel}
                  value={`${formatCurrency(estimatedSavings)} / ${roiContent.annually}`}
                  emphasis
                />
                <ResultRow
                  label={roiContent.greipCostsResultLabel}
                  value={`${formatCurrency(greipCosts)} / ${roiContent.annually}`}
                />
                <ResultRow
                  label={roiContent.minReductionLabel}
                  value={`${minimumReduction}%`}
                />
                <ResultRow
                  label={roiContent.fraudLossesResultLabel}
                  value={`${formatCurrency(fraudLosses)} / ${roiContent.annually}`}
                />
              </dl>
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-6">
          <ArrowLink href={localizeHref(roiContent.caseStudiesHref, locale)}>
            {roiContent.caseStudiesLabel}
          </ArrowLink>
        </Reveal>
      </Container>
    </section>
  );
}
